import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Play, Pause } from "lucide-react";
import { useRef } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionHeader } from "@/components/SongCard";

export function PerfCard({ perf }) {
  const { user } = useAuth();
  const [likes, setLikes] = useState(perf.likes || 0);
  const [liked, setLiked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const ownerSrc = perf.owner?.avatar_url ? (perf.owner.avatar_url.startsWith("http") ? perf.owner.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${perf.owner.avatar_url}`) : "";

  const toggleLike = async (e) => {
    e.preventDefault();
    if (!user) return;
    const { data } = await api.post(`/recordings/${perf.recording_id}/like`);
    setLiked(data.liked); setLikes(data.likes);
  };
  const togglePlay = (e) => {
    e.preventDefault();
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause(); else audioRef.current.play();
    setPlaying(!playing);
  };

  return (
    <Link to={`/performance/${perf.recording_id}`} className="card-surface rounded-2xl overflow-hidden hover-lift block" data-testid={`perf-${perf.recording_id}`}>
      <div className="relative aspect-video">
        <img src={perf.cover_url} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center group">
          <div className="w-12 h-12 rounded-full bg-neon-pink/90 flex items-center justify-center group-hover:scale-110 transition-transform">{playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}</div>
        </button>
        {(perf.is_duet || perf.duet_open) && <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-neon-cyan/90 text-black">{perf.is_duet ? "Duet" : "Open Duet"}</span>}
        <audio ref={audioRef} src={`${API}/recordings/${perf.recording_id}/audio`} onEnded={() => setPlaying(false)} />
      </div>
      <div className="p-4">
        <h4 className="font-head font-bold text-sm truncate">{perf.song_title}</h4>
        <p className="text-xs text-muted-foreground truncate">{perf.song_artist}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full bg-neon-purple/30 flex items-center justify-center text-[10px] overflow-hidden flex-shrink-0">{ownerSrc ? <img src={ownerSrc} className="w-full h-full object-cover" alt="" /> : (perf.owner?.username || "?")[0]?.toUpperCase()}</div>
            <span className="text-xs text-muted-foreground truncate">@{perf.owner?.username}</span>
          </div>
          <button onClick={toggleLike} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-neon-pink"><Heart className={`w-4 h-4 ${liked ? "fill-neon-pink text-neon-pink" : ""}`} /> {likes}</button>
        </div>
      </div>
    </Link>
  );
}

export default function Community() {
  const [perfs, setPerfs] = useState([]);
  const [singers, setSingers] = useState([]);

  useEffect(() => {
    api.get("/community/feed", { params: { limit: 24 } }).then(({ data }) => setPerfs(data.items));
    api.get("/community/singers").then(({ data }) => setSingers(data.items));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl sm:text-5xl font-black tracking-tighter mb-2">Community</h1>
      <p className="text-muted-foreground mb-10">Discover singers and performances from around the world.</p>

      {singers.length > 0 && (
        <>
          <SectionHeader title="Global Top Singers" />
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 mb-12">
            {singers.map((s) => (
              <Link key={s.username} to={`/@${s.username}`} className="w-32 flex-shrink-0 text-center group">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-2 border-white/10 group-hover:border-neon-pink transition-colors bg-neon-purple/20 flex items-center justify-center">
                  {s.avatar_url ? <img src={s.avatar_url.startsWith("http") ? s.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${s.avatar_url}`} className="w-full h-full object-cover" alt="" /> : <span className="text-2xl font-head font-bold">{(s.display_name || s.username)[0].toUpperCase()}</span>}
                </div>
                <p className="text-sm font-semibold mt-2 truncate">@{s.username}</p>
                <p className="text-xs text-muted-foreground">{s.followers} · {s.public_recordings} songs</p>
              </Link>
            ))}
          </div>
        </>
      )}

      <SectionHeader title="Latest Performances" />
      {perfs.length === 0 ? (
        <div className="card-surface rounded-2xl p-12 text-center text-muted-foreground">No public performances yet. <Link to="/record" className="text-neon-cyan">Record the first one →</Link></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{perfs.map((p) => <PerfCard key={p.recording_id} perf={p} />)}</div>
      )}
    </div>
  );
}
